import { Anchor, Badge, Button, Group, Select, Stack, Table, Text, Title } from '@mantine/core';
import { IconPlus } from '@tabler/icons-react';
import { useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useOrgs } from '../api/orgs';
import { useRoles } from '../api/roles';

export function RolesPage() {
  const [params, setParams] = useSearchParams();
  const { data: orgs } = useOrgs();

  // Empty `org` means "all orgs" — the default landing view now lists
  // every role with an Organisation column. Selecting one in the
  // dropdown narrows to that org and unlocks the Create-role button.
  const org = params.get('org') ?? '';
  const query = useRoles(org || undefined);

  // Decorate the (id-only) role rows with the org's display name. The
  // orgs fetch is cached at app level so this is cheap, and a missing
  // entry falls back to the raw id.
  const orgNameById = useMemo(() => {
    const m = new Map<string, string>();
    for (const o of orgs ?? []) m.set(o.id, o.name);
    return m;
  }, [orgs]);

  function patchOrg(orgId: string) {
    const next = new URLSearchParams(params);
    if (orgId) next.set('org', orgId);
    else next.delete('org');
    setParams(next, { replace: true });
  }

  return (
    <Stack gap="lg">
      <Group justify="space-between" align="flex-end">
        <Title order={2}>Roles</Title>
        <Button
          component={Link}
          to={org ? `/roles/new?org=${encodeURIComponent(org)}` : '/roles'}
          leftSection={<IconPlus size={16} />}
          // Roles are org-scoped at create-time. Pick an org before
          // creating; the unfiltered view is read-only.
          disabled={!org}
        >
          Create role
        </Button>
      </Group>

      <Group gap="md" align="flex-end" wrap="wrap">
        <Select
          label="Organisation"
          placeholder="All organisations"
          value={org || null}
          onChange={(v) => patchOrg(v ?? '')}
          data={(orgs ?? []).map((o) => ({ value: o.id, label: o.name }))}
          searchable
          clearable
          w={260}
        />
      </Group>

      <Table highlightOnHover withTableBorder>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Name</Table.Th>
            <Table.Th>Organisation</Table.Th>
            <Table.Th>Description</Table.Th>
            <Table.Th>Default</Table.Th>
            <Table.Th>Permissions</Table.Th>
            <Table.Th />
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {(query.data ?? []).map((r) => (
            <Table.Tr key={r.id}>
              <Table.Td>{r.name}</Table.Td>
              <Table.Td>{orgNameById.get(r.organisation_id) ?? r.organisation_id}</Table.Td>
              <Table.Td>{r.description ?? '—'}</Table.Td>
              <Table.Td>
                {r.is_default ? (
                  <Badge color="blue" variant="light">
                    default
                  </Badge>
                ) : (
                  '—'
                )}
              </Table.Td>
              <Table.Td>
                {r.permissions.length} permission{r.permissions.length === 1 ? '' : 's'}
              </Table.Td>
              <Table.Td>
                <Anchor component={Link} to={`/roles/${r.id}/edit`} size="sm">
                  open
                </Anchor>
              </Table.Td>
            </Table.Tr>
          ))}
          {(query.data ?? []).length === 0 && !query.isPending && (
            <Table.Tr>
              <Table.Td colSpan={6}>
                <Text c="dimmed" ta="center">
                  {org ? 'No roles yet for this organisation.' : 'No roles configured yet.'}
                </Text>
              </Table.Td>
            </Table.Tr>
          )}
        </Table.Tbody>
      </Table>
    </Stack>
  );
}
